import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useParams } from 'react-router-dom';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import config from './config';
import { getOrganizationContext, getOrganizationHeaders } from './orgContext';

// ==================== INTERFACES ====================

interface AssignmentDto {
  id: number;
  type: string;
  title: string;
  description?: string | null;
  status: string;
  dueAt?: string | null;
  timeLimitMinutes?: number | null;
  maxAttempts: number;
  allowLateSubmission: boolean;
  publishedAt?: string | null;
  questionCount: number;
  targetCount: number;
  createdAt: string;
  updatedAt: string;
}

interface AssignmentHomeworkQuestion {
  id: number;
  questionId: number;
  displayOrder: number;
  marks: number;
  negativeMarks: number;
  isRequired: boolean;
  questionText: string;
  questionLatex?: string | null;
  optionA?: string | null;
  optionB?: string | null;
  optionC?: string | null;
  optionD?: string | null;
  optionE?: string | null;
  isMultipleChoice: boolean;
}

interface AssignmentHomeworkSummary {
  id: number;
  examId?: number | null;
  type: string;
  title: string;
  description?: string | null;
  dueAt?: string | null;
  timeLimitMinutes?: number | null;
  maxAttempts: number;
  allowLateSubmission: boolean;
  attemptCount: number;
  attemptsRemaining: number;
}

interface HomeworkPayload {
  assignment: AssignmentHomeworkSummary;
  questions: AssignmentHomeworkQuestion[];
}

interface HomeworkAnswer {
  questionId: number;
  selectedOption?: string;
  answerText?: string;
}

interface AttemptAnswer {
  questionId: number;
  studentSelectedOption?: string;
  studentAnswerText?: string;
  isCorrect?: boolean;
  teacherComment?: string;
  marks?: number;
  status?: string; // pending, saved, submitted
}

interface AttemptDetails {
  id: number;
  status: string;
  startedAt: string;
  submittedAt?: string;
  reviewedAt?: string;
  rawScore?: number;
  totalMarks?: number;
  percentage?: number;
  answers: AttemptAnswer[];
}

interface AiExplanationStep {
  stepNumber?: number;
  explanation?: string;
  equationLatex?: string;
}

interface AiExplanationPayload {
  title?: string;
  steps?: AiExplanationStep[];
  finalAnswer?: string;
  checks?: string[];
  tips?: string[];
}

interface AssignmentStatus {
  assignmentId: number;
  status: 'not-started' | 'in-progress' | 'submitted' | 'reviewed';
  lastAttempt?: { submittedAt: string; score?: number };
}

// ==================== DRAWING CANVAS ====================

const DrawingCanvas: React.FC<{ canvasRef: React.RefObject<HTMLCanvasElement | null>; onQuestionChange: () => void }> = ({ canvasRef }) => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [penColor, setPenColor] = useState('#000000');
  const [penSize, setPenSize] = useState(2);
  const [tool, setTool] = useState<'pen' | 'eraser'>('pen');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = canvas.offsetWidth;
    canvas.height = window.innerWidth <= 480 ? 250 : window.innerWidth <= 768 ? 300 : 400;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, canvas.width, canvas.height);
  }, [canvasRef]);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : (e as React.MouseEvent).clientX - rect.left;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : (e as React.MouseEvent).clientY - rect.top;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : (e as React.MouseEvent).clientX - rect.left;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : (e as React.MouseEvent).clientY - rect.top;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (tool === 'eraser') {
      ctx.clearRect(x - penSize / 2, y - penSize / 2, penSize, penSize);
    } else {
      ctx.strokeStyle = penColor;
      ctx.lineWidth = penSize;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.lineTo(x, y);
      ctx.stroke();
    }
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, canvas.width, canvas.height);
  };

  return (
    <div style={{ marginBottom: 20, borderRadius: 6, border: '2px solid #ff9800', backgroundColor: '#fff', overflow: 'hidden' }}>
      <div style={{ backgroundColor: '#fff8f0', padding: 12, borderBottom: '1px solid #ff9800', fontSize: 12, color: '#666' }}>
        📝 Write down your steps here. Use the pen to draw and eraser to correct.
      </div>
      <div style={{ padding: 12, backgroundColor: '#fafafa', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={() => setTool('pen')} style={{ padding: '6px 12px', backgroundColor: tool === 'pen' ? '#ff9800' : '#e0e0e0', color: tool === 'pen' ? '#fff' : '#333', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>✏️ Pen</button>
        <button onClick={() => setTool('eraser')} style={{ padding: '6px 12px', backgroundColor: tool === 'eraser' ? '#ff9800' : '#e0e0e0', color: tool === 'eraser' ? '#fff' : '#333', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>🧹 Eraser</button>
        <input type="color" value={penColor} onChange={(e) => setPenColor(e.target.value)} disabled={tool === 'eraser'} style={{ width: 40, height: 32, border: '1px solid #ccc', borderRadius: 4, cursor: tool === 'eraser' ? 'not-allowed' : 'pointer' }} />
        <select value={penSize} onChange={(e) => setPenSize(Number(e.target.value))} style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #ccc', fontSize: 12 }}>
          <option value={1}>Thin</option>
          <option value={2}>Normal</option>
          <option value={4}>Medium</option>
          <option value={6}>Thick</option>
        </select>
        <button onClick={clearCanvas} style={{ padding: '6px 12px', backgroundColor: '#f44336', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600, marginLeft: 'auto' }}>🗑️ Clear</button>
      </div>
      <canvas
        ref={canvasRef}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
        style={{ display: 'block', width: '100%', height: window.innerWidth <= 480 ? 250 : window.innerWidth <= 768 ? 300 : 400, cursor: tool === 'pen' ? 'crosshair' : 'default', touchAction: 'none' }}
      />
    </div>
  );
};

// ==================== COMPONENT ====================

const AssignmentHomework: React.FC = () => {
  const { assignmentId, organizationSlug } = useParams<{ assignmentId: string; organizationSlug?: string }>();
  const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  const containsLatexLikeMath = (text: string): boolean => {
    return /\\[a-zA-Z]+|[A-Za-z0-9]\^\{[^}]+\}|[A-Za-z0-9]_\{[^}]+\}/.test(text);
  };

  const convertLatexToHtml = (text: string): string => {
    if (!text) return text;

    text = text.replace(/\\textbf\{([^}]*)\}/g, '<strong>$1</strong>');
    text = text.replace(/\\textit\{([^}]*)\}/g, '<em>$1</em>');
    text = text.replace(/\\underline\{([^}]*)\}/g, '<u>$1</u>');
    text = text.replace(/\\texttt\{([^}]*)\}/g, '<code>$1</code>');
    text = text.replace(/\\text\{([^}]*)\}/g, '$1');

    return text;
  };

  const formatInlineLatexForMathJax = (text: string): string => {
    if (!text) return text;

    if (/\\\(|\\\[|\$\$?|\\begin\{/.test(text)) {
      return text;
    }

    if (!containsLatexLikeMath(text)) {
      return text;
    }

    const colonIndex = text.indexOf(':');
    if (colonIndex >= 0) {
      const prefix = text.slice(0, colonIndex + 1);
      const suffix = text.slice(colonIndex + 1).trim();

      if (containsLatexLikeMath(suffix)) {
        return `${prefix} \\(${suffix}\\)`;
      }
    }

    return `\\(${text}\\)`;
  };

  // Screen size detection
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 640 : false
  );

  // Organization
  const [resolvedOrganizationId, setResolvedOrganizationId] = useState<number | null>(null);
  const [resolvingOrganization, setResolvingOrganization] = useState(false);

  // Data
  const [assignments, setAssignments] = useState<AssignmentDto[]>([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<number | null>(null);
  const [payload, setPayload] = useState<HomeworkPayload | null>(null);
  const [payloadLoading, setPayloadLoading] = useState(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null);
  const [attemptId, setAttemptId] = useState<number | null>(null);
  const [attemptDetails, setAttemptDetails] = useState<AttemptDetails | null>(null);

  // Answers & State
  const [answers, setAnswers] = useState<Record<number, HomeworkAnswer>>({});
  const [unsavedAnswers, setUnsavedAnswers] = useState<Record<number, HomeworkAnswer>>({});
  const [autoSaveNotification, setAutoSaveNotification] = useState<string | null>(null);

  // UI
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [showPracticeArea, setShowPracticeArea] = useState(false);
  const [loadingExplanation, setLoadingExplanation] = useState(false);
  const [aiExplanation, setAiExplanation] = useState<string | null>(null);
  const [aiExplanationJson, setAiExplanationJson] = useState<AiExplanationPayload | null>(null);
  const [aiExplanationSource, setAiExplanationSource] = useState<'json' | 'text' | null>(null);
  const [explanationError, setExplanationError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Dialogs & Submit
  const [submitting, setSubmitting] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingAssignmentId, setPendingAssignmentId] = useState<number | null>(null);
  const [showSubmitSummary, setShowSubmitSummary] = useState(false);
  const [submitMode, setSubmitMode] = useState<'all' | 'single' | null>(null);

  const parsedAssignmentId = Number(assignmentId);
  const homeworkAssignments = useMemo(
    () => assignments.filter((assignment) => (assignment.type || '').trim().toLowerCase() !== 'exam'),
    [assignments]
  );

  // ==================== FETCH ATTEMPT DETAILS ====================

  const fetchAttemptDetails = useCallback(async (attId: number) => {
    try {
      const token = await getAccessTokenSilently();
      const orgHeaders = getOrganizationHeaders();
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
        ...orgHeaders,
      };

      if (resolvedOrganizationId) {
        headers['X-Organization-Id'] = String(resolvedOrganizationId);
      }

      const response = await fetch(`${config.apiBaseUrl}/api/assignments/attempts/${attId}/details`, { headers });

      if (response.ok) {
        const data = await response.json();
        console.log('[FETCH DETAILS] Response from server:', { attemptId: attId, answers: data.answers });
        
        setAttemptDetails(data);

        // Merge answers from server with local answers to preserve user input
        setAnswers((prevAnswers) => {
          const updatedAnswers = { ...prevAnswers };
          data.answers?.forEach((answer: any) => {
            const normalizedSelectedOption = answer.selectedOption ?? answer.studentSelectedOption ?? answer.SelectedOption ?? answer.StudentSelectedOption;
            const normalizedAnswerText = answer.answerText ?? answer.studentAnswerText ?? answer.AnswerText ?? answer.StudentAnswerText;
            const normalizedStatus = answer.status ?? answer.Status;

            console.log(`[FETCH DETAILS] Processing answer Q${answer.questionId}:`, {
              normalizedSelectedOption,
              normalizedAnswerText,
              normalizedStatus
            });

            if (normalizedSelectedOption || normalizedAnswerText) {
              updatedAnswers[answer.questionId] = {
                questionId: answer.questionId,
                selectedOption: normalizedSelectedOption,
                answerText: normalizedAnswerText,
              };
            }
          });
          console.log('[FETCH DETAILS] Updated answers state:', updatedAnswers);
          return updatedAnswers;
        });
      }
    } catch (err) {
      console.error('Failed to fetch attempt details:', err);
    }
  }, [getAccessTokenSilently, resolvedOrganizationId]);

  // ==================== SCREEN SIZE ====================

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ==================== ORGANIZATION ====================

  useEffect(() => {
    if (!isAuthenticated) {
      setResolvedOrganizationId(null);
      return;
    }

    const resolveOrganization = async () => {
      if (!organizationSlug) {
        setResolvedOrganizationId(getOrganizationContext()?.organizationId ?? null);
        return;
      }

      setResolvingOrganization(true);
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${config.apiBaseUrl}/api/organizations/my`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          setResolvedOrganizationId(null);
          setError('Unable to resolve organization context for this route.');
          return;
        }

        const organizations = await response.json();
        const matched = Array.isArray(organizations)
          ? organizations.find((item: any) => String(item?.organizationSlug || '').toLowerCase() === organizationSlug.toLowerCase())
          : null;

        setResolvedOrganizationId(matched?.organizationId ? Number(matched.organizationId) : null);
        if (!matched?.organizationId) {
          setError('You do not have access to this organization context.');
        }
      } catch {
        setResolvedOrganizationId(null);
        setError('Unable to resolve organization context for this route.');
      } finally {
        setResolvingOrganization(false);
      }
    };

    void resolveOrganization();
  }, [getAccessTokenSilently, isAuthenticated, organizationSlug]);

  // ==================== FETCH ASSIGNMENTS LIST ====================

  useEffect(() => {
    if (!isAuthenticated || (organizationSlug && (resolvingOrganization || !resolvedOrganizationId))) {
      return;
    }

    const fetchAssignments = async () => {
      try {
        setAssignmentsLoading(true);
        setError(null);

        const token = await getAccessTokenSilently();
        const orgHeaders = getOrganizationHeaders();
        const headers: Record<string, string> = {
          Authorization: `Bearer ${token}`,
          ...orgHeaders,
        };

        if (resolvedOrganizationId) {
          headers['X-Organization-Id'] = String(resolvedOrganizationId);
        }

        const response = await fetch(`${config.apiBaseUrl}/api/assignments/my`, { headers });

        if (!response.ok) {
          throw new Error('Failed to load assignments.');
        }

        const data = await response.json();
        const assignmentsList = Array.isArray(data) ? data : [];
        setAssignments(assignmentsList);

        const homeworkOnly = assignmentsList.filter((assignment) => (assignment.type || '').trim().toLowerCase() !== 'exam');

        // If URL has a homework assignment id, select it; otherwise leave the list unselected.
        const requestedAssignment = homeworkOnly.find((assignment) => assignment.id === parsedAssignmentId);
        if (requestedAssignment) {
          setSelectedAssignmentId(parsedAssignmentId);
        } else {
          setSelectedAssignmentId(null);
        }
      } catch (err: any) {
        setError(err?.message || 'Failed to load assignments.');
      } finally {
        setAssignmentsLoading(false);
      }
    };

    void fetchAssignments();
  }, [getAccessTokenSilently, isAuthenticated, organizationSlug, parsedAssignmentId, resolvedOrganizationId, resolvingOrganization]);

  // ==================== FETCH QUESTIONS FOR SELECTED ASSIGNMENT ====================

  useEffect(() => {
    if (!isAuthenticated || !selectedAssignmentId || (organizationSlug && (resolvingOrganization || !resolvedOrganizationId))) {
      return;
    }

    const loadHomework = async () => {
      try {
        setPayloadLoading(true);
        setError(null);
        setAnswers({});
        setUnsavedAnswers({});
        setSelectedQuestionId(null);
        setAttemptDetails(null);
        setAiExplanation(null);
        setAiExplanationJson(null);
        setAiExplanationSource(null);
        setExplanationError(null);

        const token = await getAccessTokenSilently();
        const orgHeaders = getOrganizationHeaders();
        const headers: Record<string, string> = {
          Authorization: `Bearer ${token}`,
          ...orgHeaders,
        };

        if (resolvedOrganizationId) {
          headers['X-Organization-Id'] = String(resolvedOrganizationId);
        }

        const response = await fetch(`${config.apiBaseUrl}/api/assignments/${selectedAssignmentId}/my-questions`, { headers });

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body?.message || 'Failed to load assignment questions.');
        }

        const data = await response.json();
        const normalized: HomeworkPayload = {
          assignment: data.assignment,
          questions: Array.isArray(data.questions) ? data.questions : [],
        };

        setPayload(normalized);
        if (normalized.questions.length > 0) {
          setSelectedQuestionId(normalized.questions[0].questionId);
        }

        let recoveredAttemptId = data.activeAttemptId ?? data.attemptId ?? null;

        if (!recoveredAttemptId) {
          try {
            const startResp = await fetch(
              `${config.apiBaseUrl}/api/assignments/${selectedAssignmentId}/attempts/start`,
              { method: 'POST', headers, body: JSON.stringify({ timeSpentSeconds: null }) }
            );

            if (startResp.ok) {
              const started = await startResp.json();
              recoveredAttemptId = started?.id ?? null;
            }
          } catch {
            recoveredAttemptId = null;
          }
        }

        if (recoveredAttemptId) {
          setAttemptId(recoveredAttemptId);

          if (Array.isArray(data.savedAnswers) && data.savedAnswers.length > 0) {
            const restored: Record<number, HomeworkAnswer> = {};
            data.savedAnswers.forEach((a: any) => {
              if (a.questionId) {
                const normalizedSelectedOption = a.selectedOption ?? a.SelectedOption ?? a.StudentSelectedOption;
                const normalizedAnswerText = a.answerText ?? a.AnswerText ?? a.StudentAnswerText;
                restored[a.questionId] = {
                  questionId: a.questionId,
                  selectedOption: normalizedSelectedOption ?? undefined,
                  answerText: normalizedAnswerText ?? undefined,
                };
              }
            });
            setAnswers(restored);
          }

          await fetchAttemptDetails(recoveredAttemptId);
        } else if (data.attemptId) {
          // Legacy: fully submitted attempt
          setAttemptId(data.attemptId);
          await fetchAttemptDetails(data.attemptId);
        }
      } catch (err: any) {
        setError(err?.message || 'Failed to load assignment.');
      } finally {
        setPayloadLoading(false);
      }
    };

    void loadHomework();
  }, [getAccessTokenSilently, isAuthenticated, organizationSlug, selectedAssignmentId, resolvedOrganizationId, resolvingOrganization, fetchAttemptDetails]);

  // ==================== SAVE ON NAVIGATE ====================

  const handleAnswerChange = (questionId: number, selectedOption?: string, answerText?: string) => {
    const newAnswer: HomeworkAnswer = { questionId, selectedOption, answerText };
    console.log(`[ANSWER CHANGE] Q${questionId}:`, newAnswer);
    setAnswers((prev) => {
      const next = { ...prev, [questionId]: newAnswer };
      return next;
    });
    setUnsavedAnswers((prev) => ({ ...prev, [questionId]: newAnswer }));
    setAutoSaveNotification(null);
  };

  // Called on Next / Previous click — saves draft if there are unsaved answers
  const saveCurrentAnswers = async (currentAnswers: Record<number, HomeworkAnswer>) => {
    if (Object.keys(unsavedAnswers).length === 0 || !selectedAssignmentId) return;

    try {
      const token = await getAccessTokenSilently();
      const orgHeaders = getOrganizationHeaders();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...orgHeaders,
      };
      if (resolvedOrganizationId) {
        headers['X-Organization-Id'] = String(resolvedOrganizationId);
      }

      let currentAttemptId = attemptId;

      // Start attempt on first save if none exists
      if (!currentAttemptId) {
        const startResp = await fetch(
          `${config.apiBaseUrl}/api/assignments/${selectedAssignmentId}/attempts/start`,
          { method: 'POST', headers, body: JSON.stringify({ timeSpentSeconds: null }) }
        );
        if (startResp.ok) {
          const started = await startResp.json();
          currentAttemptId = started.id;
          setAttemptId(started.id);
        } else {
          const errBody = await startResp.text();
          console.error('Start attempt error:', errBody);
          return;
        }
      }

      const answerRecords = Object.values(currentAnswers)
        .filter((a) => a && (a.selectedOption || a.answerText))
        .map((a) => ({
          questionId: a.questionId,
          selectedOption: a.selectedOption || null,
          answerText: a.answerText || null,
        }));

      const saveResp = await fetch(
        `${config.apiBaseUrl}/api/assignments/attempts/${currentAttemptId}/save-draft`,
        { method: 'PUT', headers, body: JSON.stringify({ timeSpentSeconds: null, answers: answerRecords }) }
      );

      if (saveResp.ok) {
        setUnsavedAnswers({});
        setAutoSaveNotification('✓ Saved');
        setTimeout(() => setAutoSaveNotification(null), 1500);
      }
    } catch {
      // Silently fail — don't block navigation
    }
  };

  // ==================== ASSIGNMENT SWITCHING ====================

  const handleAssignmentSelect = (assignId: number) => {
    if (assignId === selectedAssignmentId) return;

    if (Object.keys(unsavedAnswers).length > 0) {
      setPendingAssignmentId(assignId);
      setShowUnsavedDialog(true);
    } else {
      setSelectedAssignmentId(assignId);
    }
  };

  const handleConfirmUnsavedDialog = (saveAndMove: boolean) => {
    if (saveAndMove && Object.keys(unsavedAnswers).length > 0) {
      setAutoSaveNotification('✓ Answers saved');
      setUnsavedAnswers({});
    }

    if (pendingAssignmentId) {
      setSelectedAssignmentId(pendingAssignmentId);
    }

    setShowUnsavedDialog(false);
    setPendingAssignmentId(null);
  };

  // ==================== SUBMIT ATTEMPT ====================

  const handleSubmitQuestion = async (questionId: number) => {
    if (!attemptId || !payload) return;
    try {
      setSubmitting(true);
      const token = await getAccessTokenSilently();
      const orgHeaders = getOrganizationHeaders();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...orgHeaders,
      };
      if (resolvedOrganizationId) {
        headers['X-Organization-Id'] = String(resolvedOrganizationId);
      }

      // Save this question answer
      const answer = answers[questionId];
      console.log(`[SUBMIT Q${questionId}] Entire answers state:`, answers);
      console.log(`[SUBMIT Q${questionId}] Answer from state:`, { answer, selectedOption: answer?.selectedOption, answerText: answer?.answerText });
      
      if (answer) {
        const payload = {
          answers: [{ questionId, selectedOption: answer.selectedOption, answerText: answer.answerText }]
        };
        console.log(`[SUBMIT Q${questionId}] Sending payload:`, JSON.stringify(payload));
        
        const saveResp = await fetch(
          `${config.apiBaseUrl}/api/assignments/attempts/${attemptId}/save-draft`,
          {
            method: 'PUT',
            headers,
            body: JSON.stringify(payload)
          }
        );
        if (!saveResp.ok) throw new Error('Failed to save question');
        
        const responseData = await saveResp.json();
        console.log(`[SUBMIT Q${questionId}] Save response:`, responseData);
      } else {
        console.log(`[SUBMIT Q${questionId}] No answer in state!`);
      }

      // Refresh attempt details to update status
      await fetchAttemptDetails(attemptId);

      // Find current index
      const currentIdx = payload.questions.findIndex(q => q.questionId === questionId);
      const isLastQuestion = currentIdx === payload.questions.length - 1;

      // Show confirmation and auto-navigate to next question
      if (isLastQuestion) {
        setAutoSaveNotification('✓ Last question saved! Click "Submit All" to complete.');
      } else {
        setAutoSaveNotification(`✓ Question ${currentIdx + 1} saved → Moving to next...`);
        // Auto-navigate to next question after brief delay
        await new Promise(r => setTimeout(r, 800));
        setSelectedQuestionId(payload.questions[currentIdx + 1].questionId);
      }
      setSubmitMode(null);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || 'Failed to save question');
    } finally {
      setSubmitting(false);
    }
  };

  const answeredCount = useMemo(() => {
    return Object.values(answers).filter(
      (a) => (a.selectedOption || '').trim().length > 0 || (a.answerText || '').trim().length > 0
    ).length;
  }, [answers]);

  const handleSubmitAttempt = async () => {
    if (Object.keys(unsavedAnswers).length > 0) {
      setAutoSaveNotification('Saving before submit...');
      setUnsavedAnswers({});
      // Wait a moment for "saving" notification
      await new Promise((r) => setTimeout(r, 500));
    }

    setShowSubmitSummary(false);

    try {
      if (!payload?.assignment) return;

      setSubmitting(true);
      const token = await getAccessTokenSilently();
      const orgHeaders = getOrganizationHeaders();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...orgHeaders,
      };

      if (resolvedOrganizationId) {
        headers['X-Organization-Id'] = String(resolvedOrganizationId);
      }

      const answerRecords = Object.values(answers).map((answer) => ({
        questionId: answer.questionId,
        selectedOption: answer.selectedOption || null,
        answerText: answer.answerText || null,
      }));

      // The submit endpoint is always POST to /attempts/{attemptId}/submit
      // attemptId should exist by this point (created on first save)
      if (!attemptId) {
        throw new Error('Attempt not initialized. Please answer at least one question first.');
      }

      const submitUrl = `${config.apiBaseUrl}/api/assignments/attempts/${attemptId}/submit`;

      const response = await fetch(submitUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ answers: answerRecords }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body?.message || 'Failed to submit assignment.');
      }

      const data = await response.json();
      setAttemptId(data.id);
      setMessage('Assignment submitted successfully!');

      // Fetch updated attempt details
      await fetchAttemptDetails(data.id);
    } catch (err: any) {
      setError(err?.message || 'Failed to submit assignment.');
    } finally {
      setSubmitting(false);
    }
  };

  // ==================== HELPERS ====================

  const selectedQuestion = useMemo(() => {
    if (!payload || !selectedQuestionId) return null;
    return payload.questions.find((q) => q.questionId === selectedQuestionId) ?? null;
  }, [payload, selectedQuestionId]);

  const selectedAssignment = useMemo(() => {
    return assignments.find((a) => a.id === selectedAssignmentId);
  }, [assignments, selectedAssignmentId]);

  const getQuestionStatus = (questionId: number) => {
    const answer = answers[questionId];
    const normalizedSelectedOption = (answer?.selectedOption || '').trim();
    const normalizedAnswerText = (answer?.answerText || '').trim();
    const hasAnswer = normalizedSelectedOption.length > 0 || normalizedAnswerText.length > 0;

    const reviewStatus = attemptDetails?.answers.find((a) => a.questionId === questionId);
    const serverStatus = (reviewStatus?.status || '').toLowerCase();

    if (serverStatus === 'submitted') {
      return hasAnswer ? '🎯 Submitted' : '⚠️ Submitted (no answer)';
    }

    if (serverStatus === 'saved') {
      return hasAnswer ? '💾 Saved' : '💾 Saved (no answer)';
    }

    if (hasAnswer) {
      return 'Answered';
    }

    return 'Not answered';
  };

  const getQuestionStatusColor = (questionId: number) => {
    const status = getQuestionStatus(questionId);
    if (status.includes('Submitted')) return '#f59e0b';
    if (status.includes('Saved')) return '#3b82f6';
    if (status === 'Answered') return '#8b5cf6';
    return '#9ca3af';
  };

  const getAssignmentStatus = (assignId: number): AssignmentStatus['status'] => {
    if (!attemptId) return 'not-started';
    if (attemptDetails?.status === 'submitted') return attemptDetails?.reviewedAt ? 'reviewed' : 'submitted';
    return 'in-progress';
  };

  const getAssignmentStatusColor = (assignId: number) => {
    const status = getAssignmentStatus(assignId);
    if (status === 'reviewed') return '#10b981';
    if (status === 'submitted') return '#f59e0b';
    if (status === 'in-progress') return '#3b82f6';
    return '#9ca3af';
  };

  const getAssignmentStatusLabel = (assignId: number) => {
    const status = getAssignmentStatus(assignId);
    if (status === 'reviewed') return 'Reviewed';
    if (status === 'submitted') return 'Submitted';
    if (status === 'in-progress') return 'In Progress';
    return 'Not Started';
  };

  const handleGetAiExplanation = async () => {
    if (!selectedQuestion) return;

    try {
      setLoadingExplanation(true);
      setExplanationError(null);
      setAiExplanation(null);
      setAiExplanationJson(null);
      setAiExplanationSource(null);

      const response = await fetch(`${config.apiBaseUrl}/api/azureopenaitest/explain-question`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          questionId: selectedQuestion.questionId,
          question: selectedQuestion.questionText || selectedQuestion.questionLatex,
          answer: '',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get explanation from AI');
      }

      const data = await response.json();
      const parsedJson = data.explanationJson && typeof data.explanationJson === 'object'
        ? (data.explanationJson as AiExplanationPayload)
        : null;

      setAiExplanation(data.explanation ?? null);
      setAiExplanationJson(parsedJson);
      setAiExplanationSource(data.explanationSource === 'json' ? 'json' : (parsedJson ? 'json' : 'text'));
    } catch (err) {
      console.error('Error getting AI explanation:', err);
      setExplanationError('Failed to get explanation. Please try again.');
    } finally {
      setLoadingExplanation(false);
    }
  };

  // ==================== RENDER ====================

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-app-page-from to-app-page-to">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="h-screen overflow-hidden flex flex-col bg-gradient-to-br from-app-page-from to-app-page-to">
        <div className="app-breadcrumb-bar">
          <div className="app-breadcrumb-row">
            <button onClick={() => navigate('/')} className="app-breadcrumb-link">Home</button>
            <span className="app-breadcrumb-sep">›</span>
            <span className="app-breadcrumb-current">Homework</span>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-3 md:p-4 lg:p-6">
          <div className="mx-auto max-w-2xl bg-white rounded-xl shadow-md border border-indigo-100 p-6 text-center">
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Homework Assignments</h2>
            <p className="text-slate-600 mb-5">Please log in to view assignments.</p>
            <button
              onClick={() => loginWithRedirect?.()}
              className="rounded-lg bg-indigo-600 px-5 py-2.5 font-semibold text-white transition-colors hover:bg-indigo-700"
            >
              Log In
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (resolvingOrganization) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-app-page-from to-app-page-to">
        <p className="text-gray-600">Resolving organization...</p>
      </div>
    );
  }

  return (
    <MathJaxContext>
      <style>{`
        .html-content img { max-width: 100%; height: auto; }
        .html-content { word-wrap: break-word; overflow-wrap: break-word; }
      `}</style>

      <div className="h-screen overflow-hidden flex flex-col bg-gradient-to-br from-app-page-from to-app-page-to">
        <div className="app-breadcrumb-bar">
          <div className="app-breadcrumb-row">
            <button onClick={() => navigate('/')} className="app-breadcrumb-link">Home</button>
            <span className="app-breadcrumb-sep">›</span>
            <span className="app-breadcrumb-current">Homework</span>
            {selectedAssignment && (
              <>
                <span className="app-breadcrumb-sep">›</span>
                <span className="app-breadcrumb-trailing truncate">{selectedAssignment.title}</span>
              </>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-3 mt-3 md:mx-6 md:mt-4 lg:mx-8 lg:mt-4 rounded-xl border border-red-300 bg-red-50 px-3 py-3 text-sm text-red-700 sm:px-4 sm:text-base">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span>{error}</span>
              <button onClick={() => setError(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#991b1b', fontSize: 18 }}>✕</button>
            </div>
          </div>
        )}

        {/* Success Message */}
        {message && (
          <div className="mx-3 mt-3 md:mx-6 md:mt-4 lg:mx-8 lg:mt-4 rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-3 text-sm text-emerald-700 sm:px-4 sm:text-base">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span>{message}</span>
              <button onClick={() => setMessage(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#166534', fontSize: 18 }}>✕</button>
            </div>
          </div>
        )}

        <div className="flex-1 min-h-0 w-full px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4 overflow-auto lg:overflow-hidden">
          {/* Main Content */}
          <div style={{ display: 'flex', height: '100%', overflow: 'hidden', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 0 : 12 }}>
          {/* LEFT PANEL: Assignment List */}
          {!isMobile && (
            <div style={{ width: 300, backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', textAlign: 'left' }}>
              <div style={{ padding: 12, backgroundColor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, backgroundColor: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                    📚
                  </div>
                  <div style={{ minWidth: 0, textAlign: 'left' }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>Assignments</p>
                  </div>
                </div>
              </div>
              <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>
                {assignmentsLoading ? (
                  <div style={{ padding: 20, textAlign: 'left', color: '#999', fontSize: 13 }}>Loading...</div>
                ) : homeworkAssignments.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'left', color: '#999', fontSize: 13 }}>No homeworks</div>
                ) : (
                  homeworkAssignments.map((assign) => (
                    <div
                      key={assign.id}
                      onClick={() => handleAssignmentSelect(assign.id)}
                      style={{
                        padding: '10px 12px',
                        marginBottom: 6,
                        backgroundColor: selectedAssignmentId === assign.id ? '#eff6ff' : '#fff',
                        border: selectedAssignmentId === assign.id ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                        borderRadius: 6,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        if (selectedAssignmentId !== assign.id) {
                          e.currentTarget.style.backgroundColor = '#f9fafb';
                          e.currentTarget.style.borderColor = '#d1d5db';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedAssignmentId !== assign.id) {
                          e.currentTarget.style.backgroundColor = '#fff';
                          e.currentTarget.style.borderColor = '#e5e7eb';
                        }
                      }}
                    >
                      <div style={{ fontWeight: 500, fontSize: 13, color: '#1f2937', marginBottom: 4 }}>{assign.title}</div>
                      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>
                        {assign.questionCount} question{assign.questionCount !== 1 ? 's' : ''}
                      </div>
                      <div style={{ display: 'inline-block', fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 3, backgroundColor: getAssignmentStatusColor(assign.id), color: '#fff' }}>
                        {getAssignmentStatusLabel(assign.id)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* MIDDLE PANEL: Questions List */}
          {!isMobile && selectedAssignment && (
            <div style={{ width: 280, backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
              <div style={{ padding: 12, backgroundColor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, backgroundColor: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                    ❓
                  </div>
                  <div style={{ minWidth: 0, textAlign: 'left' }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>Questions</p>
                    <p style={{ margin: '2px 0 0', fontSize: 12, fontWeight: 600, color: '#6b7280' }}>{payload?.questions.length || 0} total</p>
                  </div>
                </div>
              </div>
              <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>
                {payloadLoading ? (
                  <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 13 }}>Loading...</div>
                ) : !payload || payload.questions.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 13 }}>No questions</div>
                ) : (
                  payload.questions.map((q, idx) => {
                    const isSelected = q.questionId === selectedQuestionId;
                    const status = getQuestionStatus(q.questionId);
                    const statusColor = getQuestionStatusColor(q.questionId);

                    return (
                      <div
                        key={q.questionId}
                        onClick={async () => {
                          await saveCurrentAnswers(answers);
                          setSelectedQuestionId(q.questionId);
                        }}
                        style={{
                          padding: '10px 12px',
                          marginBottom: 6,
                          backgroundColor: isSelected ? '#eff6ff' : '#fff',
                          border: isSelected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                          borderRadius: 6,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = '#f9fafb';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = '#fff';
                          }
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>Q{idx + 1}</span>
                          <span style={{ fontSize: 10, fontWeight: 600, color: statusColor }}>{status}</span>
                        </div>
                        <div style={{ fontSize: 12, color: '#4b5563', lineHeight: 1.4, maxHeight: 60, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {q.questionText}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* RIGHT PANEL: Question Content & Answer */}
          <div style={{ flex: 1, backgroundColor: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 24px rgba(15,23,42,0.06)' }}>
            {!selectedAssignment || !payload || !selectedQuestion ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', textAlign: 'center', padding: 20 }}>
                <div>
                  {!selectedAssignment ? 'Select a homework to begin' : payloadLoading ? 'Loading questions...' : 'Select a question to start'}
                </div>
              </div>
            ) : (
              <>
                {/* Scrollable area */}
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: isMobile ? 16 : 24, paddingBottom: 8 }}>
                  {/* Header row: Question N + Help / Report */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <h3 style={{ fontSize: isMobile ? 16 : 18, fontWeight: 400, margin: 0, color: '#222' }}>
                      Question {(payload.questions.findIndex((q) => q.questionId === selectedQuestion.questionId) + 1)}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {/* Help */}
                      <button
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '5px 10px', borderRadius: 7,
                          border: '1.5px solid #c7d2fe', background: '#fff',
                          color: '#6366f1', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#eef2ff'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                      >
                        📚 Help
                      </button>
                      {/* Report */}
                      <button
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '5px 10px', borderRadius: 7,
                          border: '1.5px solid #fca5a5', background: '#fff',
                          color: '#ef4444', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                      >
                        🚩 Report
                      </button>
                      {/* Review status badge */}
                      {(() => {
                        const rs = attemptDetails?.answers.find((a) => a.questionId === selectedQuestion.questionId);
                        if (rs?.status === 'submitted' && rs?.isCorrect !== undefined) {
                          return (
                            <div style={{
                              display: 'flex', alignItems: 'center', gap: 6,
                              padding: '5px 10px', borderRadius: 6,
                              backgroundColor: rs.isCorrect ? '#e8f5e9' : '#ffebee',
                              border: `2px solid ${rs.isCorrect ? '#4caf50' : '#f44336'}`,
                            }}>
                              <span style={{ fontSize: 16, fontWeight: 'bold', color: rs.isCorrect ? '#4caf50' : '#f44336' }}>
                                {rs.isCorrect ? '✓' : '✗'}
                              </span>
                              <span style={{ fontSize: 11, fontWeight: 600, color: rs.isCorrect ? '#2e7d32' : '#c62828' }}>
                                {rs.isCorrect ? 'Correct' : 'Incorrect'}
                              </span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>

                  {/* Question text */}
                  <div style={{ marginBottom: isMobile ? 24 : 40, paddingTop: isMobile ? 0 : 8, textAlign: 'left' }}>
                    {selectedQuestion.questionText && (
                      <div
                        className="html-content"
                        style={{ fontSize: isMobile ? 14 : 16, fontWeight: 600, color: '#0f172a', lineHeight: 1.6 }}
                        dangerouslySetInnerHTML={{ __html: selectedQuestion.questionText }}
                      />
                    )}
                    {selectedQuestion.questionLatex && (
                      <div style={{ marginTop: 8, padding: 12, backgroundColor: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                        <MathJax dynamic>{`$$${selectedQuestion.questionLatex}$$`}</MathJax>
                      </div>
                    )}
                  </div>

                  {/* Show Practice Area button */}
                  <div style={{ marginBottom: 20, textAlign: 'left' }}>
                    <button
                      onClick={() => setShowPracticeArea(!showPracticeArea)}
                      style={{
                        padding: '10px 16px',
                        backgroundColor: '#ff9800', color: '#fff',
                        border: 'none', borderRadius: 6,
                        cursor: 'pointer', fontSize: 14, fontWeight: 600,
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        transition: 'background-color 0.2s ease',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f57c00'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ff9800'; }}
                    >
                      ✏️ {showPracticeArea ? 'Hide' : 'Show'} Practice Area
                    </button>
                    {showPracticeArea && (
                      <DrawingCanvas
                        canvasRef={canvasRef}
                        onQuestionChange={() => {
                          const canvas = canvasRef.current;
                          if (!canvas) return;
                          const ctx = canvas.getContext('2d');
                          if (!ctx) return;
                          ctx.fillStyle = '#ffffff';
                          ctx.fillRect(0, 0, canvas.width, canvas.height);
                          ctx.strokeStyle = '#e0e0e0';
                          ctx.lineWidth = 1;
                          ctx.strokeRect(0, 0, canvas.width, canvas.height);
                        }}
                      />
                    )}
                  </div>

                  {/* YOUR ANSWER label + options */}
                  <div style={{ marginBottom: 28, textAlign: 'left' }}>
                    <p style={{ fontSize: 12, color: '#999', marginBottom: 8, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>
                      Your Answer
                    </p>
                    {(() => {
                      const questionAnswer = attemptDetails?.answers.find((a) => a.questionId === selectedQuestion.questionId);
                      const isSubmitted = questionAnswer?.status === 'submitted' || attemptDetails?.status === 'submitted' || attemptDetails?.status === 'AutoEvaluated';
                      const currentAnswer = answers[selectedQuestion.questionId];
                      if (selectedQuestion.isMultipleChoice) {
                        return (
                          <div style={{
                            border: '1px solid #e0e0e0',
                            borderRadius: 6, padding: 10, backgroundColor: '#fff',
                            opacity: isSubmitted ? 0.7 : 1,
                          }}>
                            {[
                              { key: 'A', value: selectedQuestion.optionA },
                              { key: 'B', value: selectedQuestion.optionB },
                              { key: 'C', value: selectedQuestion.optionC },
                              { key: 'D', value: selectedQuestion.optionD },
                              { key: 'E', value: selectedQuestion.optionE },
                            ].filter((opt) => opt.value).map((opt) => (
                              <label
                                key={opt.key}
                                style={{
                                  display: 'flex', alignItems: 'flex-start', gap: 8,
                                  padding: '8px 4px',
                                  cursor: isSubmitted ? 'not-allowed' : 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                }}
                              >
                                <input
                                  type="radio"
                                  name={`q${selectedQuestion.questionId}`}
                                  checked={currentAnswer?.selectedOption === opt.key}
                                  onChange={() => { if (!isSubmitted) handleAnswerChange(selectedQuestion.questionId, opt.key); }}
                                  disabled={isSubmitted}
                                  style={{ marginTop: 3, cursor: isSubmitted ? 'not-allowed' : 'pointer' }}
                                />
                                <span style={{ fontSize: isMobile ? 13 : 16, color: '#1f2937', lineHeight: 1.5 }}>
                                  <strong>{opt.key}.</strong>{' '}
                                  <MathJax inline dynamic>
                                    <span dangerouslySetInnerHTML={{ __html: opt.value || '' }} />
                                  </MathJax>
                                </span>
                              </label>
                            ))}
                          </div>
                        );
                      }
                      return (
                        <textarea
                          value={currentAnswer?.answerText || ''}
                          onChange={(e) => { if (!isSubmitted) handleAnswerChange(selectedQuestion.questionId, undefined, e.target.value); }}
                          disabled={isSubmitted}
                          placeholder="Type your answer here..."
                          style={{
                            width: '100%',
                            minHeight: isMobile ? 80 : 100,
                            padding: isMobile ? 10 : 12,
                            fontSize: isMobile ? 13 : 14,
                            border: '1px solid #e0e0e0',
                            borderRadius: 6,
                            fontFamily: 'inherit',
                            resize: 'vertical',
                            boxSizing: 'border-box',
                            opacity: isSubmitted ? 0.7 : 1,
                            cursor: isSubmitted ? 'not-allowed' : 'text',
                          }}
                        />
                      );
                    })()}
                  </div>

                  {/* Auto-save indicator */}
                  {autoSaveNotification && (
                    <div style={{ marginBottom: 12, fontSize: 12, color: '#10b981', fontWeight: 500 }}>
                      {autoSaveNotification}
                    </div>
                  )}

                  {/* Teacher feedback box (after review) */}
                  {(() => {
                    const rs = attemptDetails?.answers.find((a) => a.questionId === selectedQuestion.questionId);
                    if (!rs?.status && !rs?.teacherComment) return null;
                    if (rs?.status && rs.status !== 'submitted' && !rs.teacherComment) return null;
                    return (
                      <div style={{ marginBottom: 20, padding: 16, backgroundColor: rs.isCorrect ? '#e8f5e9' : '#ffebee', borderRadius: 6, border: `1px solid ${rs.isCorrect ? '#c8e6c9' : '#ffcdd2'}` }}>
                        <p style={{ fontSize: 12, color: rs.isCorrect ? '#2e7d32' : '#c62828', marginBottom: 6, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {rs.isCorrect ? '✓ Teacher Feedback' : '✗ Teacher Feedback'}
                        </p>
                        {rs.marks !== undefined && (
                          <p style={{ fontSize: 13, color: rs.isCorrect ? '#2e7d32' : '#c62828', marginBottom: rs.teacherComment ? 6 : 0, fontWeight: 600 }}>
                            Score: {rs.marks}
                          </p>
                        )}
                        {rs.teacherComment && (
                          <p style={{ fontSize: 14, color: rs.isCorrect ? '#1b5e20' : '#b71c1c', lineHeight: 1.6, fontStyle: 'italic', margin: 0 }}>
                            "{rs.teacherComment}"
                          </p>
                        )}
                      </div>
                    );
                  })()}

                  {/* Show Explanation button — only after the whole attempt is submitted */}
                  {(() => {
                    const rs = attemptDetails?.answers.find((a) => a.questionId === selectedQuestion.questionId);
                    const status = attemptDetails?.status?.toLowerCase() || '';
                    const isAttemptSubmitted = status === 'submitted' || status === 'autoevaluated' || status === 'reviewed' || status === 'completed' || status === 'finished';
                    if (!isAttemptSubmitted) return null;
                    return (
                      <div style={{ marginBottom: 20, textAlign: 'left' }}>
                        <button
                          onClick={handleGetAiExplanation}
                          disabled={loadingExplanation}
                          style={{
                            padding: '10px 16px',
                            backgroundColor: loadingExplanation ? '#94a3b8' : '#6366f1',
                            color: '#fff', border: 'none', borderRadius: 6,
                            cursor: loadingExplanation ? 'not-allowed' : 'pointer',
                            fontSize: 14, fontWeight: 600,
                            transition: 'background-color 0.2s ease',
                          }}
                          onMouseEnter={(e) => { if (!loadingExplanation) e.currentTarget.style.backgroundColor = '#4f46e5'; }}
                          onMouseLeave={(e) => { if (!loadingExplanation) e.currentTarget.style.backgroundColor = '#6366f1'; }}
                        >
                          {loadingExplanation ? 'Loading...' : '📚 Show Explanation'}
                        </button>

                        {loadingExplanation && (
                          <div style={{ marginTop: 12, textAlign: 'left', padding: 12, backgroundColor: '#eef2ff', borderRadius: 6, border: '1px solid #c7d2fe', color: '#3730a3', fontSize: 13, fontWeight: 600 }}>
                            Generating step-by-step explanation...
                          </div>
                        )}

                        {explanationError && (
                          <div style={{ marginTop: 12, textAlign: 'left', padding: 12, backgroundColor: '#ffebee', color: '#c62828', borderRadius: 6 }}>
                            {explanationError}
                          </div>
                        )}

                        {(aiExplanationJson || aiExplanation) && (
                          <div style={{ marginTop: 12, padding: 16, backgroundColor: '#eef2ff', borderRadius: 8, border: '1px solid #c7d2fe' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                              <p style={{ fontSize: 12, color: '#3730a3', margin: 0, textTransform: 'uppercase', fontWeight: 700 }}>Explanation</p>
                              {aiExplanationSource && (
                                <span style={{ fontSize: 11, color: '#4338ca', backgroundColor: '#e0e7ff', border: '1px solid #c7d2fe', borderRadius: 999, padding: '2px 8px', fontWeight: 600 }}>
                                  Source: {aiExplanationSource.toUpperCase()}
                                </span>
                              )}
                            </div>

                            {aiExplanationJson ? (
                              <div style={{ fontSize: 14, color: '#333', lineHeight: 1.65, fontFamily: 'inherit' }}>
                                {aiExplanationJson.title && (
                                  <p style={{ marginTop: 0, marginBottom: 10, fontWeight: 700, color: '#312e81' }}>{aiExplanationJson.title}</p>
                                )}

                                {Array.isArray(aiExplanationJson.steps) && aiExplanationJson.steps.length > 0 && (
                                  <div style={{ marginBottom: 12 }}>
                                    {aiExplanationJson.steps.map((step, index) => (
                                      <div key={`step-${index}`} style={{ marginBottom: 10 }}>
                                        <p style={{ margin: 0, fontWeight: 600, color: '#1f2937' }}>
                                          Step {step.stepNumber ?? index + 1}
                                        </p>
                                        {step.explanation && (
                                          <p style={{ margin: '4px 0 0 0' }}>
                                            <MathJax dynamic>{formatInlineLatexForMathJax(step.explanation)}</MathJax>
                                          </p>
                                        )}
                                        {step.equationLatex && (
                                          <MathJax dynamic>{`$$${step.equationLatex}$$`}</MathJax>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {aiExplanationJson.finalAnswer && (
                                  <p style={{ margin: '8px 0', fontWeight: 700, color: '#065f46' }}>
                                    <MathJax dynamic>{`Final Answer: ${formatInlineLatexForMathJax(aiExplanationJson.finalAnswer)}`}</MathJax>
                                  </p>
                                )}

                                {Array.isArray(aiExplanationJson.checks) && aiExplanationJson.checks.length > 0 && (
                                  <div style={{ marginTop: 8 }}>
                                    <p style={{ margin: '0 0 4px 0', fontWeight: 600, color: '#334155' }}>Checks</p>
                                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                                      {aiExplanationJson.checks.map((check, index) => (
                                        <li key={`check-${index}`}>
                                          <MathJax dynamic>{formatInlineLatexForMathJax(check)}</MathJax>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {Array.isArray(aiExplanationJson.tips) && aiExplanationJson.tips.length > 0 && (
                                  <div style={{ marginTop: 8 }}>
                                    <p style={{ margin: '0 0 4px 0', fontWeight: 600, color: '#334155' }}>Tips</p>
                                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                                      {aiExplanationJson.tips.map((tip, index) => (
                                        <li key={`tip-${index}`}>{tip}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            ) : aiExplanation ? (
                              <MathJax>
                                <div className="html-content" style={{ fontSize: 14, color: '#1e1b4b', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: convertLatexToHtml(aiExplanation) }} />
                              </MathJax>
                            ) : null}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Fixed bottom: Prev / Next / Submit */}
                <div style={{
                  display: 'flex', gap: 12,
                  padding: '10px 16px',
                  borderTop: '1px solid #e5e7eb',
                  backgroundColor: '#fff',
                  flexShrink: 0,
                }}>
                  {/* Answered counter */}
                  <div style={{ display: 'flex', alignItems: 'center', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap', paddingRight: 8, borderRight: '1px solid #e5e7eb' }}>
                    {answeredCount}/{payload.questions.length}
                  </div>
                  <button
                    onClick={async () => {
                      const idx = payload.questions.findIndex((q) => q.questionId === selectedQuestion.questionId);
                      if (idx > 0) {
                        await saveCurrentAnswers(answers);
                        setSelectedQuestionId(payload.questions[idx - 1].questionId);
                      }
                    }}
                    disabled={payload.questions.findIndex((q) => q.questionId === selectedQuestion.questionId) === 0}
                    style={{
                      flex: 1, padding: '10px 16px',
                      backgroundColor: payload.questions.findIndex((q) => q.questionId === selectedQuestion.questionId) === 0 ? '#cbd5e1' : '#334155',
                      color: '#fff', border: 'none', borderRadius: 6,
                      cursor: payload.questions.findIndex((q) => q.questionId === selectedQuestion.questionId) === 0 ? 'not-allowed' : 'pointer',
                      fontSize: 14, fontWeight: 600,
                      transition: 'background-color 0.2s ease',
                    }}
                    onMouseEnter={(e) => { if (payload.questions.findIndex((q) => q.questionId === selectedQuestion.questionId) > 0) e.currentTarget.style.backgroundColor = '#1e293b'; }}
                    onMouseLeave={(e) => { if (payload.questions.findIndex((q) => q.questionId === selectedQuestion.questionId) > 0) e.currentTarget.style.backgroundColor = '#334155'; }}
                  >
                    ← Previous
                  </button>
                  <button
                    onClick={async () => {
                      const idx = payload.questions.findIndex((q) => q.questionId === selectedQuestion.questionId);
                      if (idx < payload.questions.length - 1) {
                        await saveCurrentAnswers(answers);
                        setSelectedQuestionId(payload.questions[idx + 1].questionId);
                      }
                    }}
                    disabled={payload.questions.findIndex((q) => q.questionId === selectedQuestion.questionId) === payload.questions.length - 1}
                    style={{
                      flex: 1, padding: '10px 16px',
                      backgroundColor: payload.questions.findIndex((q) => q.questionId === selectedQuestion.questionId) === payload.questions.length - 1 ? '#cbd5e1' : '#334155',
                      color: '#fff', border: 'none', borderRadius: 6,
                      cursor: payload.questions.findIndex((q) => q.questionId === selectedQuestion.questionId) === payload.questions.length - 1 ? 'not-allowed' : 'pointer',
                      fontSize: 14, fontWeight: 600,
                      transition: 'background-color 0.2s ease',
                    }}
                    onMouseEnter={(e) => { if (payload.questions.findIndex((q) => q.questionId === selectedQuestion.questionId) < payload.questions.length - 1) e.currentTarget.style.backgroundColor = '#1e293b'; }}
                    onMouseLeave={(e) => { if (payload.questions.findIndex((q) => q.questionId === selectedQuestion.questionId) < payload.questions.length - 1) e.currentTarget.style.backgroundColor = '#334155'; }}
                  >
                    Next →
                  </button>
                  {payload.questions.length > 0 && (!attemptDetails || attemptDetails.status !== 'submitted') && !getQuestionStatus(selectedQuestion.questionId).includes('Submitted') && (
                    <>
                      <button
                        onClick={() => { handleSubmitQuestion(selectedQuestion.questionId); }}
                        disabled={submitting || !answers[selectedQuestion.questionId]}
                        style={{
                          flex: 1, padding: '10px 16px',
                          backgroundColor: submitting || !answers[selectedQuestion.questionId] ? '#cbd5e1' : '#f59e0b',
                          color: '#fff', border: 'none', borderRadius: 6,
                          cursor: submitting || !answers[selectedQuestion.questionId] ? 'not-allowed' : 'pointer',
                          fontSize: 13, fontWeight: 600,
                          transition: 'background-color 0.2s ease',
                        }}
                        onMouseEnter={(e) => { if (!submitting && answers[selectedQuestion.questionId]) e.currentTarget.style.backgroundColor = '#d97706'; }}
                        onMouseLeave={(e) => { if (!submitting && answers[selectedQuestion.questionId]) e.currentTarget.style.backgroundColor = '#f59e0b'; }}
                        title="Save this question's answer"
                      >
                        {submitting ? '⏳' : '✓ Submit'}
                      </button>
                      
                      {payload.questions.findIndex((q) => q.questionId === selectedQuestion.questionId) === payload.questions.length - 1 && (
                        <button
                          onClick={() => {
                            if (answeredCount === payload.questions.length) {
                              setShowSubmitSummary(true);
                            } else {
                              alert(`Please answer all questions. Answered: ${answeredCount}/${payload.questions.length}`);
                            }
                          }}
                          disabled={submitting || answeredCount !== payload.questions.length}
                          style={{
                            flex: 1, padding: '10px 16px',
                            backgroundColor: submitting || answeredCount !== payload.questions.length ? '#cbd5e1' : '#10b981',
                            color: '#fff', border: 'none', borderRadius: 6,
                            cursor: submitting || answeredCount !== payload.questions.length ? 'not-allowed' : 'pointer',
                            fontSize: 13, fontWeight: 600,
                            transition: 'background-color 0.2s ease',
                          }}
                          onMouseEnter={(e) => { if (!submitting && answeredCount === payload.questions.length) e.currentTarget.style.backgroundColor = '#059669'; }}
                          onMouseLeave={(e) => { if (!submitting && answeredCount === payload.questions.length) e.currentTarget.style.backgroundColor = '#10b981'; }}
                          title={answeredCount === payload.questions.length ? 'Submit entire assignment' : `Answer all questions first (${answeredCount}/${payload.questions.length})`}
                        >
                          {submitting ? 'Submitting...' : '🎯 Submit All'}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      </div>

      {/* Unsaved Changes Dialog */}
      {showUnsavedDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 8, padding: 24, maxWidth: 400, boxShadow: '0 20px 25px rgba(0,0,0,0.15)' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#1f2937' }}>Unsaved Changes</h3>
            <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 20, lineHeight: 1.6 }}>
              You have unsaved answers. Do you want to save them before switching to another assignment?
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => handleConfirmUnsavedDialog(false)}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  backgroundColor: '#f3f4f6',
                  color: '#1f2937',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Don't Save
              </button>
              <button
                onClick={() => handleConfirmUnsavedDialog(true)}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  backgroundColor: '#3b82f6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Save & Switch
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submit Summary Dialog */}
      {showSubmitSummary && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 8, padding: 24, maxWidth: 400, boxShadow: '0 20px 25px rgba(0,0,0,0.15)' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#1f2937' }}>Submit Assignment</h3>
            <div style={{ backgroundColor: '#f3f4f6', borderRadius: 6, padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14 }}>
                <span style={{ color: '#6b7280' }}>Questions Answered:</span>
                <span style={{ fontWeight: 600, color: '#1f2937' }}>{answeredCount} / {payload?.questions.length}</span>
              </div>
              {answeredCount < payload?.questions.length! && (
                <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 8 }}>
                  ⚠️ {payload!.questions.length - answeredCount} question{payload!.questions.length - answeredCount !== 1 ? 's' : ''} not answered
                </div>
              )}
            </div>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>
              Once submitted, your answers will be sent for review. You won't be able to make changes.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setShowSubmitSummary(false)}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  backgroundColor: '#f3f4f6',
                  color: '#1f2937',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitAttempt}
                disabled={submitting}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  backgroundColor: submitting ? '#9ca3af' : '#10b981',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {submitting ? 'Submitting...' : 'Confirm Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </MathJaxContext>
  );
};

export default AssignmentHomework;
